import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Shield, Scale, Landmark, Users } from 'lucide-react';
import axios from 'axios';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useCountUp = (end, duration = 2000, inView = true) => {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [end, duration, inView]);

  return count;
};

const AnimatedStat = ({ icon: Icon, label, value, suffix, prefix, desc, delay, inView, customDisplay }) => {
  const count = useCountUp(value, 2200 + delay * 200, inView);
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.4 + delay * 0.15 }}
      className="border-2 border-[#D8D3CB] bg-white p-6 flex items-center gap-5 hover:border-[#0F1B2D] transition-colors"
    >
      <div className="w-12 h-12 border-2 border-[#0F1B2D] flex items-center justify-center flex-shrink-0">
        <Icon size={22} className="text-[#0F1B2D]" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold">{label}</p>
        <p className="font-['Playfair_Display'] text-2xl font-semibold text-[#0F1B2D] tabular-nums">
          {customDisplay || <>{prefix}{count.toLocaleString()}{suffix}</>}
        </p>
      </div>
      <p className="text-xs text-[#4A4640] font-medium max-w-[120px] text-right">{desc}</p>
    </motion.div>
  );
};

const HeroSection = ({ onWaitlistClick }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [memberCount, setMemberCount] = useState(140);
  const { formatAmount, lang } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then(({ data }) => {
      if (data.count > 0) setMemberCount(data.count + 140);
    }).catch(() => {});
  }, []);

  // 47 Cr INR = 470,000,000 INR base value
  const disputeAmountFormatted = formatAmount(470000000, { compact: true });

  const t = (en, hi) => lang === 'hi' ? hi : en;

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="h-[2px] w-12 bg-[#C4A35A]" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#0F1B2D] font-bold">
                {t('Est. for Creative Professionals', 'रचनात्मक पेशेवरों के लिए स्थापित')}
              </p>
            </div>

            <h1
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0F1B2D] leading-[1.1] tracking-tight mb-10"
              data-testid="hero-heading"
            >
              {t('Before you enter', 'मंच पर जाने से पहले,')}
              <br />
              {t('the stage, look into', 'देखो अपना')}
              <span className="block text-[#C4A35A] italic">
                {t('the mirror.', 'आईना।')}
              </span>
            </h1>

            <p className="text-base sm:text-lg text-[#4A4640] font-medium leading-relaxed mb-10 max-w-lg">
              {t(
                'AINAA is the professional body for creative professionals. We maintain verified trust records, provide legal protection for your work, and offer financial support — backed by a credibility score calculated from real data, not self-reported claims.',
                'AINAA रचनात्मक पेशेवरों का संघ है। हम सत्यापित विश्वास रिकॉर्ड रखते हैं, आपके काम के लिए कानूनी सुरक्षा प्रदान करते हैं, और वित्तीय सहायता देते हैं — वास्तविक डेटा पर आधारित विश्वसनीयता स्कोर के साथ।'
              )}
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={onWaitlistClick}
                className="btn-ainaa-primary px-8 py-4 text-sm font-semibold flex items-center gap-2.5 uppercase tracking-wider"
                data-testid="hero-apply-btn"
              >
                {t('Apply for Membership', 'सदस्यता के लिए आवेदन करें')}
                <ArrowRight size={16} />
              </button>
              <a
                href="#services"
                className="btn-ainaa-secondary px-8 py-4 text-sm font-semibold uppercase tracking-wider"
                data-testid="hero-learn-btn"
              >
                {t('Our Services', 'हमारी सेवाएँ')}
              </a>
            </div>
          </motion.div>

          {/* Right — Animated stats */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="hidden lg:block relative"
          >
            <div className="space-y-5" data-testid="hero-stats">
              <AnimatedStat icon={Users} label={t('Verified Members', 'सत्यापित सदस्य')} value={memberCount} suffix="+" prefix="" desc={t('Artists with verified credentials', 'सत्यापित कलाकार')} delay={0} inView={inView} />
              <AnimatedStat icon={Scale} label={t('Disputes Recovered', 'विवाद वसूली')} value={47} suffix="" prefix="" desc={t('Value recovered for members', 'सदस्यों के लिए मूल्य')} delay={1} inView={inView}
                customDisplay={disputeAmountFormatted}
              />
              <AnimatedStat icon={Landmark} label={t('Cases Resolved', 'मामले सुलझे')} value={312} suffix="" prefix="" desc={t('IP & contract disputes settled', 'IP और अनुबंध विवाद')} delay={2} inView={inView} />
              <AnimatedStat icon={Shield} label={t('Verification Rate', 'सत्यापन दर')} value={98} suffix="%" prefix="" desc={t('Accuracy in trust scoring', 'स्कोर सटीकता')} delay={3} inView={inView} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
